import styled from 'styled-components';

export const PanelContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 400px;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transform: translateX(0);
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const PanelHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #333;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

export const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

export const Section = styled.div`
  margin-bottom: 30px;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  font-weight: 600;
`;

export const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

export const SettingLabel = styled.label`
  font-size: 14px;
  color: #666;
`;

export const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 100px;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

export const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

export const Toggle = styled.button<{ active: boolean }>`
  width: 48px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#4CAF50' : '#ccc'};
  position: relative;
  cursor: pointer;
  transition: background 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.3s;
  }
`;

export const CodeContainer = styled.div`
  background: #2d2d2d;
  border-radius: 8px;
  padding: 20px;
  position: relative;
  margin-top: 20px;
`;

export const CodeBlock = styled.pre`
  margin: 0;
  color: #f8f8f2;
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

export const CopyButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2980b9;
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const PreviewContainer = styled.div`
  width: 100%;
  height: 400px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
  position: relative;
`;

export const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

export const Alert = styled.div<{ type: 'info' | 'warning' | 'success' }>`
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;

  ${props => props.type === 'info' && `
    background: #e3f2fd;
    color: #1976d2;
    border: 1px solid #90caf9;
  `}

  ${props => props.type === 'warning' && `
    background: #fff3e0;
    color: #f57c00;
    border: 1px solid #ffb74d;
  `}

  ${props => props.type === 'success' && `
    background: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #81c784;
  `}
`;

export const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: #3498db;
    color: white;

    &:hover {
      background: #2980b9;
    }
  ` : `
    background: white;
    color: #3498db;
    border: 1px solid #3498db;

    &:hover {
      background: #f0f8ff;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

export const Tab = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#3498db' : 'transparent'};
  color: ${props => props.active ? '#3498db' : '#666'};
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #3498db;
  }
`;